import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  Alert,
  Chip,
} from '@mui/material';
import { 
  Edit, 
  Delete, 
  Add, 
  Assignment,
  Search,
  Info,
  CheckCircle,
  Warning,
  CalendarToday,
  Category,
  Description,
  Update
} from '@mui/icons-material';

const FormMaster = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [addFormMasterDialogOpen, setAddFormMasterDialogOpen] = useState(false);
  const [editFormMasterDialogOpen, setEditFormMasterDialogOpen] = useState(false);
  const [selectedFormMaster, setSelectedFormMaster] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Form state for add/edit
  const [formMasterData, setFormMasterData] = useState({
    formType: '',
    formName: '',
  });

  // Sample form master data
  const [formMasterList, setFormMasterList] = useState([
    {
      id: 1,
      formType: 'Employee Registration Form',
      formName: 'Employee Onboarding Form',
      createdDate: '2024-01-15',
      status: 'Active',
    },
    {
      id: 2,
      formType: 'Plot Booking Form',
      formName: 'Plot Booking Application',
      createdDate: '2024-02-20',
      status: 'Active',
    },
    {
      id: 3,
      formType: 'Payment Receipt Form',
      formName: 'Payment Confirmation Receipt',
      createdDate: '2024-03-10',
      status: 'Active',
    },
    {
      id: 4,
      formType: 'Customer Registration Form',
      formName: 'Customer Registration Application',
      createdDate: '2024-04-05',
      status: 'Active',
    },
    {
      id: 5,
      formType: 'Site Visit Form',
      formName: 'Site Visit Booking Form',
      createdDate: '2024-05-12',
      status: 'Inactive',
    },
    {
      id: 6,
      formType: 'Complaint Registration Form',
      formName: 'Customer Complaint Form',
      createdDate: '2024-06-18',
      status: 'Active',
    },
    {
      id: 7,
      formType: 'Employee Registration Form',
      formName: 'HR Employee Registration',
      createdDate: '2024-07-22',
      status: 'Active',
    },
    {
      id: 8,
      formType: 'Plot Booking Form',
      formName: 'Online Plot Booking',
      createdDate: '2024-08-14',
      status: 'Active',
    },
  ]);

  // Available form types
  const formTypes = [
    'Employee Registration Form',
    'Plot Booking Form',
    'Payment Receipt Form',
    'Customer Registration Form',
    'Site Visit Form',
    'Complaint Registration Form',
    'Document Verification Form',
    'Loan Application Form',
  ];

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleAddFormMaster = () => {
    setFormMasterData({ formType: '', formName: '' });
    setAddFormMasterDialogOpen(true);
  };

  const handleEditFormMaster = (formMaster) => {
    setSelectedFormMaster(formMaster);
    setFormMasterData({
      formType: formMaster.formType,
      formName: formMaster.formName,
    });
    setEditFormMasterDialogOpen(true);
  };

  const handleDeleteFormMaster = (formMasterId) => {
    if (window.confirm('Are you sure you want to delete this form master?')) {
      setFormMasterList(prev => prev.filter(item => item.id !== formMasterId));
      console.log('Deleting form master:', formMasterId);
    }
  };

  const handleFormMasterDataChange = (field, value) => {
    setFormMasterData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveFormMaster = () => {
    if (!formMasterData.formType.trim() || !formMasterData.formName.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    const newFormMaster = {
      id: Math.max(...formMasterList.map(item => item.id)) + 1,
      formType: formMasterData.formType,
      formName: formMasterData.formName,
      createdDate: new Date().toISOString().split('T')[0],
      status: 'Active',
    };

    setFormMasterList(prev => [...prev, newFormMaster]);
    setAddFormMasterDialogOpen(false);
    setFormMasterData({ formType: '', formName: '' });
    setSuccessMessage('Form master added successfully!');
  };

  const handleUpdateFormMaster = () => {
    if (!formMasterData.formType.trim() || !formMasterData.formName.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setFormMasterList(prev =>
      prev.map(item =>
        item.id === selectedFormMaster.id
          ? { 
              ...item, 
              formType: formMasterData.formType,
              formName: formMasterData.formName 
            }
          : item
      )
    );

    setEditFormMasterDialogOpen(false);
    setFormMasterData({ formType: '', formName: '' });
    setSelectedFormMaster(null);
    setSuccessMessage('Form master updated successfully!');
  };

  const getFilteredFormMasterData = () => {
    let filteredData = formMasterList;

    // Filter by table search query
    if (tableSearchQuery.trim()) {
      filteredData = filteredData.filter(formMaster =>
        Object.values(formMaster).some(value =>
          String(value).toLowerCase().includes(tableSearchQuery.toLowerCase())
        )
      );
    }

    // Filter by tab selection
    if (tabValue === 1) {
      filteredData = filteredData.filter(formMaster => formMaster.status === 'Active');
    } else if (tabValue === 2) {
      filteredData = filteredData.filter(formMaster => formMaster.status === 'Inactive');
    }

    return filteredData;
  };

  const getFilteredByStatus = (status) => {
    return formMasterList.filter(formMaster => formMaster.status === status);
  };

  const getSearchSummary = () => {
    const filteredData = getFilteredFormMasterData();
    return `${filteredData.length} form master(s) found`;
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Active':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      case 'Inactive':
        return {
          backgroundColor: '#f44336',
          color: 'white',
        };
      default:
        return {};
    }
  };

  // Summary calculations
  const totalFormMasters = formMasterList.length;
  const activeCount = getFilteredByStatus('Active').length;
  const inactiveCount = getFilteredByStatus('Inactive').length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Assignment sx={{ color: '#1976d2', fontSize: 32 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
          Form Master Management
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle sx={{ fontSize: 20 }} />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Assignment sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Form Masters
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {totalFormMasters}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <CheckCircle sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Active Form Masters
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {activeCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#ffebee', 
            border: '1px solid #ffcdd2',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Warning sx={{ color: '#d32f2f', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Inactive Form Masters
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {inactiveCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Card */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Category sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Form Master List
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Search Form Masters..."
                variant="outlined"
                size="small"
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleAddFormMaster}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2
                }}
              >
                Add Form Master
              </Button>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ fontSize: 20 }} />
              {getSearchSummary()}
            </Box>
          </Alert>

          {/* Tab Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="form master status tabs">
              <Tab label="All Form Masters" />
              <Tab label="Active" />
              <Tab label="Inactive" />
            </Tabs>
          </Box>
        
        {getFilteredFormMasterData().length > 0 ? (
          <TableContainer sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Form Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Form Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Created Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredFormMasterData()
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((formMaster, index) => (
                    <TableRow key={formMaster.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{page * rowsPerPage + index + 1}</TableCell>
                      
                      {/* Form Type */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Category sx={{ color: '#666', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                            {formMaster.formType}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Form Name */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Description sx={{ color: '#666', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                            {formMaster.formName}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Created Date */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday sx={{ color: '#666', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                            📅 {formMaster.createdDate}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={formMaster.status}
                          sx={{
                            ...getStatusStyle(formMaster.status),
                            fontWeight: 600,
                            fontSize: '12px'
                          }}
                          size="small"
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Form Master">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEditFormMaster(formMaster)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Form Master">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeleteFormMaster(formMaster.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={getFilteredFormMasterData().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        ) : (
          <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Warning sx={{ fontSize: 20 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  No Form Masters Found
                </Typography>
                <Typography variant="body2">
                  No form masters match your search criteria.
                </Typography>
              </Box>
            </Box>
          </Alert>
        )}
        </CardContent>
      </Card>

      {/* Add Form Master Dialog */}
      <Dialog open={addFormMasterDialogOpen} onClose={() => setAddFormMasterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Add sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
              Add New Form Master
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Form Type *</InputLabel>
                  <Select
                    value={formMasterData.formType}
                    label="Form Type *"
                    onChange={(e) => handleFormMasterDataChange('formType', e.target.value)}
                    required
                  >
                    {formTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Form Name *"
                  value={formMasterData.formName}
                  onChange={(e) => handleFormMasterDataChange('formName', e.target.value)}
                  placeholder="Enter form name"
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFormMasterDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveFormMaster} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Form Master Dialog */}
      <Dialog open={editFormMasterDialogOpen} onClose={() => setEditFormMasterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Update sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
              Edit Form Master
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Form Type *</InputLabel>
                  <Select
                    value={formMasterData.formType}
                    label="Form Type *"
                    onChange={(e) => handleFormMasterDataChange('formType', e.target.value)}
                    required
                  >
                    {formTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Form Name *"
                  value={formMasterData.formName}
                  onChange={(e) => handleFormMasterDataChange('formName', e.target.value)}
                  placeholder="Enter form name"
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFormMasterDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpdateFormMaster} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormMaster;